import { Typography } from "components/atoms";
import dayjs from "dayjs";
import classNames from "classnames";
import {
  ArrowDownIcon,
  CalenderIcon,
  DeleteIcon,
  Spinner,
} from "components/atoms/Icon";
import {
  useDeleteEventMutation,
  useGetStudyPlannerEventQuery,
  studyPlanner,
} from "services/studyPlanner";
import { FetchError } from "../FetchErrorMsg";
import { YellowClock } from "components/atoms/Icon/YellowClock";
import { Select } from "components/atoms/Icon/Select";
import { EmptyCircle } from "components/atoms/Icon/EmptyCircle";
import { useNavigate } from "react-router-dom";
import { formatDateDuration } from "utils/functions/formatDateDuration";
import { useEffect, useState } from "react";
import { ErrorMobileModal } from "components/pages/StudyPlannerPage/ErrorMobileModal";
import { openExternalUrl } from "utils/functions/openExternalUrl";
import { useAppDispatch } from "hooks";
import { RefreshModal } from "components/molecules/RefreshModal";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
  id: number;
  toggleView: any;
  editable: any;
}

export const StudyPlannerScheduleDetailedCard = ({
  theme,
  id,
  toggleView,
  editable,
}: IProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, isError, data, error, refetch: refetchStudyPlannerEvent }: any =
    useGetStudyPlannerEventQuery(
      {
        eventId: id,
      },
      { skip: id === undefined || id === null }
    );

  const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);

  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState({
    isOpen: false,
    data: "",
  });

  const [contentObjectType, setContentObjectType] = useState<string | undefined>("");

  const [deleteItem, { isLoading: isLoadingDelete, isError: isErrorDelete }] =
    useDeleteEventMutation();

  const handleDeleteItem = async (id: number) => {
    try {
      await deleteItem({ eventId: id }).unwrap();
    } catch (error: any) {
      console.log("error : ", error);
    }
  };

  const handleRefreshModalOpen = (obj: any) => {
    const contentType = obj.ContentObjectType || "Test";
    setContentObjectType(contentType);
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: true,
      data: contentType,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: false,
      data: "",
    }));
  };

  const handleRefresh = async () => {
    const promises = [
      refetchStudyPlannerEvent && refetchStudyPlannerEvent(),
    ];

    Promise.allSettled(promises).then(() => {
      handleRefreshModalClose();
    });
    
    // Invalidate cache to ensure fresh data
    dispatch(studyPlanner.util.invalidateTags(["StudyPlannerUpdateEvent"]));
  };

  const handleItemClick = (obj: any) => {
    if (
      obj.ContentObjectType === "OfflineDrill" ||
      obj.ContentObjectType === "OfflineReading"
    ) {
      navigate("/drill", { state: obj });
    } else {
      openExternalUrl({
        url: obj.LaunchUrl,
        contentObjectType: obj.ContentObjectType,
        dispatch,
        onRefreshModalOpen: () => {
          handleRefreshModalOpen(obj);
        },
      });
    }
  };

  useEffect(() => {
    if (isError && error) {
      setIsOpenErrorModal(true);
    }
  }, [isError]);

  return (
    <div className="schedule-detailed-section" key={id}>
      {isLoading || isFetching || isLoadingDelete ? (
        <Spinner theme={theme} />
      ) : isError || isErrorDelete ? (
        error && error.data ? (
          <ErrorMobileModal
            isOpen={isOpenErrorModal}
            handleOnclose={() => setIsOpenErrorModal(false)}
            theme={theme}
            openBottom={true}
            message={error.data.Message}
          />
        ) : (
          <FetchError content="Oops! failed to load resources." />
        )
      ) : (
        data && (
          <>
            <div
              className={classNames({
                "top-detailed-section": true,
                "diagnostic-exam": data.EventType === "Diagnostic",
                "homework-tile": data.EventType === "Homework",
                "additional-tile":
                  data.EventType !== "Homework" &&
                  data.EventType !== "Diagnostic" &&
                  data.EventType !== "TestSegment" &&
                  data.EventType !== "ClassSegment" &&
                  data.EventType !== "TestDay" &&
                  data.EventType !== "Prework",
              })}
            >
              <div className="close-icon-row">
                <ArrowDownIcon
                  theme={theme}
                  color={theme === "light" ? "#9D9D9D" : "#12131A"}
                  onClick={() => toggleView(false)}
                />
              </div>
              <div className="first-row">
                <div className="top-left-content">
                  <Typography
                    styles="title"
                    label={data.Title}
                    variant="h7Bold"
                  />
                </div>
                <div className="top-right-content">
                  <Typography
                    label={`aprox: ${
                      data.TimeEstimate === 0 || data.TimeEstimate === null
                        ? "0 min"
                        : formatDateDuration(data.TimeEstimate)
                    }`}
                    variant="h9Medium"
                    styles="top-right-content-typography-custom-style"
                  />
                </div>
              </div>
              {data.EventType === "Homework" ||
              data.EventType === "Diagnostic" ||
              data.EventType === "Prework" ? (
                <div className="second-row">
                  <Typography
                    label={
                      data.EventType &&
                      data.EventType.replace(/([A-Z])/g, " $1").trim()
                    }
                    variant="h8Med"
                    styles="second-row-typography-custom-style"
                  />
                </div>
              ) : null}
              <div className="third-row">
                <div className="left-section">
                  <div className="scheduled-date-section">
                    <Typography
                      label="Scheduled Date"
                      variant="h8Med"
                      styles="second-row-typography-custom-style"
                    />
                  </div>
                  <div className="calender-section">
                    <Typography
                      label={dayjs(data.ScheduledDate).format("MM/DD/YYYY")}
                      variant="h8Book"
                      styles="second-row-typography-custom-style"
                    />
                    <CalenderIcon
                      color={theme === "light" ? "black" : "white"}
                      onClick={() =>
                        editable === true &&
                        navigate("/reschedule-event", {
                          state: { cardData: data },
                        })
                      }
                    />
                  </div>
                </div>
                {data.IsDelitable === true ? (
                  <div className="right-section">
                    <DeleteIcon
                      onClick={() => handleDeleteItem(data.StudyPlannerEventId)}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="bottom-icon-text-section">
              {data.Items && Array.isArray(data.Items)
                ? data.Items.map((obj: any, index: number) => (
                    <div className="icon-text-wrapper" key={index}>
                      {obj.Status === "Inprogress" ||
                      obj.Status === "InProgress" ||
                      obj.Status === "In-Progress" ? (
                        <div className="icon-wrapper">
                          <YellowClock />
                        </div>
                      ) : obj.Status === "Complete" ||
                        obj.Status === "Completed" ? (
                        <div className="icon-wrapper">
                          <Select />
                        </div>
                      ) : obj.Status === "Todo" || obj.Status === "Pending" ? (
                        <div className="icon-wrapper">
                          <EmptyCircle theme={theme} />
                        </div>
                      ) : (
                        <div className="icon-wrapper">
                          <Select />
                        </div>
                      )}
                      <Typography
                        variant="h8Med"
                        label={obj.Title}
                        onClick={() => handleItemClick(obj)}
                        styles="launch-item-text"
                      />
                    </div>
                  ))
                : null}
            </div>
          </>
        )
      )}
      <RefreshModal
        isOpen={isRefreshModalOpen.isOpen}
        handleOnClose={handleRefreshModalClose}
        theme={theme}
        contentObjectType={
          isRefreshModalOpen.data !== ""
            ? isRefreshModalOpen.data
            : contentObjectType
        }
        isFetching={isFetching}
        handleRefresh={handleRefresh}
      />
    </div>
  );
};
